import React, { useEffect, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';
import RexiPopup from '../../../components/RexiPopup';
import '../DataTableTemplate.css';
import './CertificationManagement.css';

const CertificationManagement = () => {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(() => null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(() => ({}));
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  const fieldMap = [
    { key: 'certifier_id', label: 'The Certifier ID you will use in your certification data' },
    { key: 'company_name_on_certificates', label: 'The company name to appear on your certificates' },
    { key: 'contact_name', label: 'Contact name' },
    { key: 'contact_telephone', label: 'Contact phone' },
    { key: 'contact_email', label: 'Contact email' },
    { key: 'contact_address_line_1', label: 'Contact address line 1' },
    { key: 'contact_address_line_2', label: 'Contact address line 2' },
    { key: 'contact_suite', label: 'Contact suite number' },
    { key: 'contact_city', label: 'Contact city' },
    { key: 'contact_state_province', label: 'Contact state/province' },
    { key: 'contact_country', label: 'Contact country' },
    { key: 'contact_postal_code', label: 'Contact zipcode' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        console.error('🔴 Auth error:', userError.message);
      }

      if (!user) {
        console.warn('⚠️ No user found — exiting fetchData');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('certification_management')
        .select('*')
        .eq('auth_id', user.id)
        .single();

      if (error) {
        console.error('❌ Supabase query error:', error.message);
        setLoading(false);
        return;
      }

      if (!data) {
        console.warn('⚠️ No certification record found for this user.');
        setLoading(false);
        return;
      }

      setRecord(data);
      setFormData(data);
      setLoading(false);
    };

    fetchData();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: typeof formData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!record) return;

    const { error } = await supabase
      .from('certification_management')
      .update(formData)
      .eq('id', record.id);

    if (error) {
      console.error('❌ Update error:', error.message);
    } else {
      setRecord({ ...formData });
      setEditing(false);
      setShowSuccessPopup(true);
    }
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }
  if (!record) return <div style={{ padding: '2rem' }}>No certification record found.</div>;

  return (
    <div className="certification-container">
      <h1 className="cert-title">Certification Management</h1>
      <div className="page-help-link" onClick={() => setShowHelpPopup(true)}>
        Ask Rexi about this page
      </div>

      {!editing && (
        <div className="top-button-container">
          <button className="primary" onClick={() => setShowConfirmPopup(true)}>
            Click to Allow Editing
          </button>
        </div>
      )}

      {showConfirmPopup && (
        <RexiPopup
          message="Remember that this data will appear in your certificate data submissions. Make sure it is accurate. This contact information cannot include personal data such as a personal phone number, email, or address."
          onClose={() => setShowConfirmPopup(false)}
          onContinue={() => { setEditing(true); setShowConfirmPopup(false); }}
          showCancel={true}
        />
      )}

      {showSuccessPopup && (
        <RexiPopup
          message="Updated successfully!"
          onClose={() => setShowSuccessPopup(false)}
          confirmText="Done"
        />
      )}

      {showHelpPopup && (
        <RexiPopup
          message="This page provides information about your company that will appear in every CPSC eFiling data set and on your certificate .pdf's. Do not share personal information here, use the contact's company email, phone, and address."
          onClose={() => setShowHelpPopup(false)}
          confirmText="Got It"
        />
      )}

      <div className="form-section">
        {fieldMap.map(({ key, label }) => {
          const value = formData[key];
          return (
            <div key={key}>
              <label>{label}</label>
              <input
                type="text"
                name={key}
                value={value != null ? String(value) : ''}
                onChange={handleChange}
                disabled={!editing}
              />
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="bottom-button-container">
          <button className="primary" onClick={handleSubmit}>Confirm Changes for CPSC eFiling</button>
          <button className="secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default CertificationManagement;
